import React, { useState, useEffect } from 'react';
import { Save, Settings as SettingsIcon, Building, Calendar, Loader2, Upload, Image, Mail, Phone, FileText, X } from 'lucide-react';
import { api } from '../services/api';
import { CompanyInfo } from '../types';

interface SettingsProps {
    companyInfo: CompanyInfo | null;
    onCompanyInfoUpdate: (info: CompanyInfo) => void;
}

const Settings: React.FC<SettingsProps> = ({ companyInfo, onCompanyInfoUpdate }) => {
    const [companyForm, setCompanyForm] = useState<CompanyInfo>({
        name_ar: '',
        name_en: '',
        address_ar: '',
        address_en: '',
        phone: '',
        fax: '',
        email: '',
        cr_number: '',
        logo_url: '',
        auto_invoice_day: 25
    });
    const [isSavingCompany, setIsSavingCompany] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (companyInfo && !isInitialized) {
            setCompanyForm({
                name_ar: companyInfo.name_ar || '',
                name_en: companyInfo.name_en || '',
                address_ar: companyInfo.address_ar || '',
                address_en: companyInfo.address_en || '',
                phone: companyInfo.phone || '',
                fax: companyInfo.fax || '',
                email: companyInfo.email || '',
                cr_number: companyInfo.cr_number || '',
                logo_url: companyInfo.logo_url || '',
                auto_invoice_day: companyInfo.auto_invoice_day || 25
            });
            setIsInitialized(true);
        }
    }, [companyInfo, isInitialized]);

    const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setCompanyForm(prev => ({ 
            ...prev, 
            [name]: name === 'auto_invoice_day' ? Number(value) : value 
        }));
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert("⚠️ حجم الملف كبير جداً! يجب ألا يتجاوز حجم الشعار 2 ميجابايت.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setCompanyForm(prev => ({ ...prev, logo_url: reader.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCompanySave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSavingCompany(true);
        try {
            const updatedInfo = {
                ...companyForm,
                auto_invoice_day: Number(companyForm.auto_invoice_day || 25)
            };
            await api.companyInfo.update(updatedInfo);
            onCompanyInfoUpdate(updatedInfo);
            alert("✅ تم حفظ إعدادات وبيانات المؤسسة بنجاح في قاعدة البيانات");
        } catch (error: any) {
            alert("❌ تعذر حفظ بيانات وإعدادات المؤسسة: " + error.message);
        } finally {
            setIsSavingCompany(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
            <div className="flex items-center gap-4 text-slate-800">
                <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-200">
                    <SettingsIcon size={32} className="text-fly-600" />
                </div>
                <div>
                    <h1 className="text-3xl font-black">إعدادات النظام</h1>
                    <p className="text-slate-500 font-medium">تخصيص خيارات النظام وبيانات المؤسسة</p>
                </div>
            </div>

            {/* نموذج بيانات الشركة والمؤسسة */}
            <form onSubmit={handleCompanySave} className="bg-white rounded-3xl p-8 border border-slate-200 shadow-xl space-y-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-3 text-slate-800">
                        <div className="bg-emerald-50 p-2 rounded-lg">
                            <Building size={20} className="text-emerald-600" />
                        </div>
                        <h3 className="text-lg font-bold">بيانات وإعدادات المؤسسة (للطباعة والفواتير الآلية)</h3>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* اللوجو */}
                    <div className="flex flex-col items-center justify-center border border-dashed border-slate-200 rounded-2xl p-6 bg-slate-50/50">
                        <label className="block text-sm font-bold text-slate-700 mb-4 text-center">شعار المؤسسة (Logo)</label>
                        {companyForm.logo_url ? (
                            <div className="relative group w-32 h-32 mb-4">
                                <img 
                                    src={companyForm.logo_url} 
                                    alt="شعار المؤسسة" 
                                    className="w-full h-full object-contain rounded-xl border border-slate-200 bg-white p-2 shadow-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => setCompanyForm(prev => ({ ...prev, logo_url: '' }))}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors shadow"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center bg-white text-slate-400 mb-4">
                                <Image size={32} className="opacity-40 mb-1" />
                                <span className="text-[10px] font-medium">لا يوجد شعار</span>
                            </div>
                        )}
                        <label className="cursor-pointer bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2">
                            <Upload size={14} />
                            <span>رفع الشعار</span>
                            <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleLogoChange}
                                className="hidden"
                            />
                        </label>
                    </div>

                    {/* الاسم والعناوين */}
                    <div className="md:col-span-2 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-bold text-slate-700">اسم المؤسسة (بالعربية)</label>
                                <input 
                                    type="text" 
                                    name="name_ar" 
                                    value={companyForm.name_ar} 
                                    onChange={handleCompanyChange}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-800"
                                    required
                                />
                            </div>
                            <div className="space-y-2" dir="ltr">
                                <label className="block text-sm font-bold text-slate-700 text-left">Company Name (English)</label>
                                <input 
                                    type="text" 
                                    name="name_en" 
                                    value={companyForm.name_en} 
                                    onChange={handleCompanyChange}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-800 text-left"
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-bold text-slate-700">العنوان بالتفصيل (بالعربية)</label>
                                <textarea 
                                    name="address_ar" 
                                    value={companyForm.address_ar} 
                                    onChange={handleCompanyChange}
                                    rows={2}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-800 resize-none"
                                />
                            </div>
                            <div className="space-y-2" dir="ltr">
                                <label className="block text-sm font-bold text-slate-700 text-left">Address Details (English)</label>
                                <textarea 
                                    name="address_en" 
                                    value={companyForm.address_en} 
                                    onChange={handleCompanyChange}
                                    rows={2}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-800 text-left resize-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-4 border-t border-slate-50">
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-slate-700 flex items-center gap-1.5"><Phone size={14} className="text-slate-400" />الهاتف</label>
                        <input 
                            type="text" 
                            name="phone" 
                            value={companyForm.phone} 
                            onChange={handleCompanyChange}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 transition-all font-bold text-slate-800"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-slate-700 flex items-center gap-1.5"><Phone size={14} className="text-slate-400" />الفاكس</label>
                        <input 
                            type="text" 
                            name="fax" 
                            value={companyForm.fax} 
                            onChange={handleCompanyChange}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 transition-all font-bold text-slate-800"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-slate-700 flex items-center gap-1.5"><Mail size={14} className="text-slate-400" />البريد الإلكتروني</label>
                        <input 
                            type="email" 
                            name="email" 
                            value={companyForm.email} 
                            onChange={handleCompanyChange}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 transition-all font-bold text-slate-800"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-slate-700 flex items-center gap-1.5"><FileText size={14} className="text-slate-400" />رقم السجل التجاري</label>
                        <input 
                            type="text" 
                            name="cr_number" 
                            value={companyForm.cr_number} 
                            onChange={handleCompanyChange}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 transition-all font-bold text-slate-800"
                        />
                    </div>
                    
                    {/* يوم إصدار الفواتير الآلي */}
                    <div className="space-y-2 md:col-span-4">
                        <label className="block text-sm font-bold text-slate-700 flex items-center gap-1.5">
                            <Calendar size={14} className="text-slate-400" />
                            <span>يوم إصدار الفواتير الآلي</span>
                        </label>
                        <select 
                            name="auto_invoice_day" 
                            value={companyForm.auto_invoice_day} 
                            onChange={handleCompanyChange}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-emerald-500 transition-all font-bold text-slate-800"
                        >
                            {[...Array(28)].map((_, i) => (
                                <option key={i+1} value={i+1}>{i+1} من كل شهر</option>
                            ))}
                        </select>
                        <p className="text-xs text-slate-400 leading-relaxed pr-1 mt-1">
                            سيقوم النظام تلقائياً وبشكل مركزي بإصدار وتوليد فواتير الإيجار الشهرية لكافة عقود الإيجار السارية في هذا اليوم المختار من كل شهر ميلادي وحفظها في قاعدة البيانات.
                        </p>
                    </div>
                </div>

                <div className="pt-6 border-t border-slate-100 flex justify-end">
                    <button 
                        type="submit" 
                        disabled={isSavingCompany}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-xl font-bold flex items-center gap-3 transition-all shadow-xl shadow-emerald-600/10 hover:shadow-2xl hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSavingCompany ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <Save size={20} />
                        )}
                        <span>حفظ الإعدادات والبيانات</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Settings;
